import React, { useEffect, useState, useRef } from "react";
import config from "../../constants/config";

const { ENV_SUFIX } = config;
const ERROR_URL = "/account-center/loginError.html?source=gigyajs-proxy";
const CDC_HOST = process.env[`REACT_APP_CDC_HOST_NAME${ENV_SUFIX}`];
const MFA_API_KEY = process.env[`REACT_APP_MFA_CDC_API_KEY${ENV_SUFIX}`];
const GIGYA_TIMEOUT_MS = 15000;

/**
 * MfaProxy — React equivalent of the original MFAproxy.html
 *
 * Flow (matches original 1:1):
 * 1. If URL has hash → strip it via full page reload (IdP tokens in hash)
 * 2. Load gigya.js with MFA API key — redirect to error page on failure
 * 3. Set sessionStorage keys (gig_apikey, referrer_state)
 * 4. Delete cip_identifier cookie
 * 5. Determine loginLink from referrer
 * 6. Check GAC cookie → oidcFlag
 * 7. Load gigya.oidc.js (both paths load it immediately — matches original behaviour)
 * 8. Show spinner until gigya redirects the user away
 */
const MfaProxy = () => {
  const [showLoginMessage, setShowLoginMessage] = useState(false);
  const isInitialized = useRef(false);

  window?.tfIdentityGlobals?.showLoader();

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (isInitialized.current) return;
    isInitialized.current = true;

    // ─── Step 1: Hash cleanup (matches original exactly) ───────────────────
    // Original: if(window.location.hash) { window.location = url_without_hash }
    // The IdP redirects back with tokens in the fragment. The original strips
    // the hash immediately (before OIDC loads). On second load the hash is gone
    // and gigya.oidc.js processes the session without hash interference.
    if (window.location.hash) {
      window.location = window.location.href.substr(
        0,
        window.location.href.indexOf("#"),
      );
      return; // Page is reloading — stop all further execution
    }

    // ─── Step 2: Load gigya.js ─────────────────────────────────────────────
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = `https://${CDC_HOST}/js/gigya.js?apiKey=${MFA_API_KEY}`;

    // Timeout: if gigya.js doesn't load in time, redirect to error page
    const timeout = setTimeout(() => {
      window.location.href = ERROR_URL;
    }, GIGYA_TIMEOUT_MS);

    script.onerror = () => {
      clearTimeout(timeout);
      window.location.href = ERROR_URL;
    };

    script.onload = () => {
      clearTimeout(timeout);
      initAfterGigya();
    };

    document.head.appendChild(script);

    function getCookie(name) {
      var re = new RegExp(name + "=([^;]+)");
      var value = re.exec(document.cookie);
      return value != null ? unescape(value[1]) : null;
    }

    function loadOIDCJs(loginLink) {
      var scriptElement = document.createElement("script");
      scriptElement.type = "text/javascript";
      scriptElement.src = `https://${CDC_HOST}/js/gigya.oidc.js?apiKey=${MFA_API_KEY}`;
      scriptElement.async = true;
      scriptElement.innerText = `{ loginURL: "${loginLink}", consentURL: "/api-gateway/identity-authorization/identity/auth/consent?isTFA=true", errorURL: "loginError.html" }`;
      document.getElementsByTagName("head")[0].appendChild(scriptElement);
    }

    function initAfterGigya() {
      // ─── Step 3: Set sessionStorage ────────────────────────────────────────
      var referrerPage = document.referrer;
      sessionStorage.setItem("gig_apikey", MFA_API_KEY);
      sessionStorage.setItem("referrer_state", referrerPage);

      // ─── Step 4: Delete cip_identifier and cip_ru cookies ─────────────────
      const cookieDomain = process.env[`REACT_APP_COOKIE_DOMAIN${ENV_SUFIX}`];
      document.cookie =
        `cip_identifier=; path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; domain=${cookieDomain};`;
      document.cookie =
        `cip_ru=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; domain=${cookieDomain};`;

      // ─── Step 5: Determine loginLink ───────────────────────────────────────
      var loginLink = "mfa-signin-identifier.html";

      if (
        referrerPage.indexOf("/mfa-signin-identifier?regType=FULL") !== -1 ||
        referrerPage.indexOf("lt?cmd=ViewCart&ShoppingCartKey") !== -1
      ) {
        loginLink = "mfa-signin-identifier?regType=FULL";
      }

      // ─── Step 6: Check GAC cookie ─────────────────────────────────────────
      var gacCookie = getCookie("gac_" + MFA_API_KEY);
      var oidcFlag = gacCookie !== "" && gacCookie !== null;

      // ─── Step 7: Load OIDC ─────────────────────────────────────────────────
      // Original behaviour: both branches call loadOIDCJs() immediately.
      // In the original HTML:
      //   if(oidcFlag) { onLogin: loadOIDCJs() } ← invokes immediately, assigns undefined
      //   else { loadOIDCJs() }
      // Result: OIDC is ALWAYS loaded immediately regardless of oidcFlag.
      if (
        oidcFlag &&
        window.gigya &&
        window.gigya.accounts &&
        window.gigya.accounts.addEventHandlers
      ) {
        window.gigya.accounts.addEventHandlers({
          onLogin: loadOIDCJs(loginLink),
        });
      } else {
        loadOIDCJs(loginLink);
      }

      // ─── Step 8: loginPopup check ─────────────────────────────────────────
      if (localStorage.getItem("loginPopup") === "open") {
        setShowLoginMessage(true);
        if (document.referrer.indexOf("proxy.html") > 0) {
          localStorage.removeItem("loginPopup");
        }
      }
    }

    return () => clearTimeout(timeout);
  }, []);

  // Set body background on mount
  useEffect(() => {
    document.body.style.background = "#827f7f";
    return () => {
      document.body.style.background = "";
    };
  }, []);

  return (
    <div className="proxy-page">
      {showLoginMessage && (
        <div
          id="loginMsg"
          className="login-message"
          style={{ textAlign: "center", marginTop: "20px" }}
        >
          <span>Verifying your credentials..</span>
          <br />
          <span>Please do not close this window.</span>
        </div>
      )}
    </div>
  );
};

export default MfaProxy;
